import { useEffect } from "react";
import { useLang } from "@/context/LanguageContext";
import { HOTLINE, EMAIL, WEBSITE, FACEBOOK_URL, YOUTUBE_URL, INSTAGRAM_URL } from "@/lib/contact";

// SEO / AEO / GEO — sets <title>, meta tags, Open Graph, structured data
export default function SEO() {
  const { lang } = useLang();

  useEffect(() => {
    const title = lang === 'en'
      ? "Naljuri Inn — Premium Eco-Resort near Jaflong, Sadapathor & Sylhet"
      : "নলজুরি ইন — জাফলং, সাদাপাথর ও সিলেটের প্রিমিয়াম ইকো-রিসোর্ট";
    const description = lang === 'en'
      ? "Aristocratic eco-retreat in Naljuri, Jaflong (Sylhet, Bangladesh). Panoramic suites from ৳2,000. Sadapathor crystal waters, the viral Naljuri cricket field, and the strategic basecamp for 10 marquee Sylhet destinations."
      : "জাফলং, সিলেটে অভিজাত ইকো-রিট্রিট। প্যানোরামিক স্যুট ২০০০ টাকা থেকে। সাদাপাথরের স্বচ্ছ জল, ভাইরাল ক্রিকেট মাঠ — সিলেটের ১০টি দর্শনীয় গন্তব্যের কৌশলগত বেসক্যাম্প।";

    document.title = title;
    document.documentElement.lang = lang;

    setMeta("description", description);
    setMeta("keywords", "Naljuri Inn, Naljuri resort, Jaflong resort, Sadapathor resort, Sylhet eco resort, Bangladesh eco resort, Banglar Pahalgam, Naljuri cricket field, Lalakhal, Bisnakandi, Pangthumai, Ratargul, premium resort Sylhet");
    setMeta("robots", "index,follow,max-image-preview:large");
    setMeta("theme-color", "#1B3022");
    setMeta("geo.region", "BD-60");          // ISO Sylhet division
    setMeta("geo.placename", "Naljuri, Jaflong, Sylhet");
    setMeta("geo.position", "25.196;92.018");
    setMeta("ICBM", "25.196, 92.018");

    // Open Graph
    setMeta("og:type", "website", "property");
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:url", WEBSITE, "property");
    setMeta("og:locale", lang === 'en' ? "en_US" : "bn_BD", "property");
    setMeta("og:locale:alternate", lang === 'en' ? "bn_BD" : "en_US", "property");
    setMeta("og:image", "https://customer-assets.emergentagent.com/job_naljuri-riverside/artifacts/n41anrxv_from%20naljuri%20inn%20resort.png", "property");
    setMeta("og:site_name", "Naljuri Inn", "property");

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", "https://customer-assets.emergentagent.com/job_naljuri-riverside/artifacts/n41anrxv_from%20naljuri%20inn%20resort.png");

    setLink("canonical", WEBSITE);
    setLink("alternate", `${WEBSITE}/?lang=en`, "en");
    setLink("alternate", `${WEBSITE}/?lang=bn`, "bn");

    // JSON-LD: Hotel + LocalBusiness + FAQPage (for AEO)
    const hotel = {
      "@context": "https://schema.org",
      "@type": "Hotel",
      "name": "Naljuri Inn",
      "description": description,
      "url": WEBSITE,
      "telephone": HOTLINE,
      "email": EMAIL,
      "image": [
        "https://customer-assets.emergentagent.com/job_naljuri-riverside/artifacts/n41anrxv_from%20naljuri%20inn%20resort.png",
        "https://customer-assets.emergentagent.com/job_ef4fdd3f-901c-4603-a956-907996740397/artifacts/wbnpn6y2_Slide_02.png",
        "https://customer-assets.emergentagent.com/job_naljuri-riverside/artifacts/p6pfnvek_Sadapathor.png"
      ],
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Beside Naljuri Zilla Parishad Dakbanglow",
        "addressLocality": "Naljuri, Jaflong",
        "addressRegion": "Sylhet",
        "postalCode": "3156",
        "addressCountry": "BD"
      },
      "geo": { "@type": "GeoCoordinates", "latitude": 25.196, "longitude": 92.018 },
      "sameAs": [FACEBOOK_URL, YOUTUBE_URL, INSTAGRAM_URL],
      "starRating": { "@type": "Rating", "ratingValue": "4" },
      "amenityFeature": [
        { "@type": "LocationFeatureSpecification", "name": "Free Wi-Fi", "value": true },
        { "@type": "LocationFeatureSpecification", "name": "Air Conditioning", "value": true },
        { "@type": "LocationFeatureSpecification", "name": "On-site Restaurant", "value": true },
        { "@type": "LocationFeatureSpecification", "name": "Concierge", "value": true },
        { "@type": "LocationFeatureSpecification", "name": "Cricket Court", "value": true },
        { "@type": "LocationFeatureSpecification", "name": "Basketball Court", "value": true },
      ],
      "priceRange": "৳2,000 – ৳3,500",
      "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "120" }
    };
    const faq = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "Where exactly is Naljuri Inn?",
          "acceptedAnswer": { "@type": "Answer", "text": "Naljuri Inn is in Naljuri, Jaflong, Jaintapur upazila, Sylhet 3156, Bangladesh — beside Naljuri Zilla Parishad Dakbanglow, three kilometres from Jaflong and 90 minutes from every major Sylhet attraction." }},
        { "@type": "Question", "name": "What is the cheapest room at Naljuri Inn?",
          "acceptedAnswer": { "@type": "Answer", "text": "The Couple Honeymoon — Economy at ৳2,000 per night (regular ৳3,000). The flagship AC Couple Plus is ৳2,500 per night with the AI Special Offer (regular ৳3,500)." }},
        { "@type": "Question", "name": "What is the Naljuri viral cricket field?",
          "acceptedAnswer": { "@type": "Answer", "text": "An amateur cricket ground three minutes from the property, ringed by the Khasi hills. Locally nicknamed \"Banglar Pahalgam\", it is one of Bangladesh's most photographed amateur grounds." }},
        { "@type": "Question", "name": "How do I reserve a stay?",
          "acceptedAnswer": { "@type": "Answer", "text": `Submit the booking form on this page or message the concierge directly on WhatsApp at ${HOTLINE}.` }}
      ]
    };
    upsertLD("ld-hotel", hotel);
    upsertLD("ld-faq", faq);
  }, [lang]);

  return null;
}

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setLink(rel, href, hreflang) {
  if (!href) return;
  const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
function upsertLD(id, payload) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(payload);
}
